/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import * as THREE from "three";

import { const_var, params } from '../../../../../redux/reducers/reducer';
import { extrudeSettings, railModel } from '../../../../../utils/Functions';
import BaseRailModel from '../../BaseRail';
import PillarRailModel from '../../Legs';
import CommonTruss from '../CommonTruss';
import { MaterialTruss } from '../../../CustomMaterial';
import { controls } from '../../../../../redux/reducers/common/eventReducer';
import { trussDimensions } from '../../../../../helpers/building_component/truss/truss.helper';

const hangRailDivision = 2 / 3;

const   AST = ({ trussType, normalMap, envMap, trussModelPosInfoArr, overlapTrussArr, hiddenBaseRailIdArr, meshDelModel_CSG, isFor  }) => {
    const roofRailDistanceY = (params.trussType != "HSST") ? 0.7 : 0.5;
    return (
        <>
            {isFor == 'center' && <BaseRailModel normalMap={normalMap} envMap={envMap} meshDelModel_CSG={meshDelModel_CSG} />}
            {isFor == 'center' && <PillarRailModel trussType={trussType} trussModelPosInfoArr={trussModelPosInfoArr} normalMap={normalMap} envMap={envMap} meshDelModel_CSG={meshDelModel_CSG} overlapTrussArr={overlapTrussArr} hiddenBaseRailIdArr={hiddenBaseRailIdArr} roofRailDistanceY={roofRailDistanceY} hangRailDivision={hangRailDivision} />}
            <TrussModel trussType={trussType} trussModelPosInfoArr={trussModelPosInfoArr} normalMap={normalMap} envMap={envMap} meshDelModel_CSG={meshDelModel_CSG} overlapTrussArr={overlapTrussArr} hiddenBaseRailIdArr={hiddenBaseRailIdArr} isFor={isFor}/>
        </>
    )
}

export default AST

const TrussModel = ({ trussType, trussModelPosInfoArr, normalMap, envMap, overlapTrussArr, hiddenBaseRailIdArr, isFor }) => {
    const obj_centerHRail = new THREE.Object3D();
    const obj_centerVRail = new THREE.Object3D();
    const obj_shortConnectRail = new THREE.Object3D();
    const obj_underTruss = new THREE.Object3D();
    const obj_sideSlopeRail = new THREE.Object3D();
    const vector = new THREE.Vector3(1, 0, 0)

    const ref_centerHRail = useRef();
    const ref_centerVRail = useRef();
    const ref_shortConnectRail = useRef();
    const ref_underTruss = useRef();
    const ref_sideSlopeRail = useRef();

    const bottomBaseRailWidth = 0.3;
    const bottomBaseRailHeight = (trussType != "HSST") ? 0.1 : 0.2;
    const connectRailWidth = 0.3;
    const connectRailHeight = 0.2;
    const shortConnectRailHeight = 0.5;
    const offset = 0.035;
    const roofRailDistanceY = (trussType != "HSST") ? 0.7 : 0.5;

    const trussDimension = trussDimensions(isFor)

    const alignPosOffset = useMemo(() => {
        if(params.p_r_s === 1 ) return const_var.roofRegularDst * 2;
        else if(params.p_r_s === 2 || params.p_r_s === 3) return 0;    
        else return 0;
    }, [params.p_r_s, trussDimension.roofPitch])
    
    const roofAlpha = Math.atan(trussDimension.roofPitch / 12);
    const roofHeight = (trussDimension.buildingWidth / 2) * (trussDimension.roofPitch / 12);
    const roofOuterRailLength = useMemo(() => (Math.sqrt(Math.pow(trussDimension.buildingWidth / 2, 2) + Math.pow(roofHeight, 2))), [trussDimension.buildingWidth, roofHeight]);
    const divider = (trussType != "HSST") ? 3 : 1.5
    const multiplier  = (trussType != "HSST") ? 2 : 1
    const roofInnerRailLength = useMemo(() => (roofOuterRailLength / divider), [roofOuterRailLength])
    const bottomBaseRailLength = (trussDimension.buildingWidth - bottomBaseRailHeight / 2 - bottomBaseRailHeight / 2) / 3 * multiplier
    //  const roofCenterRailHeight = ((trussDimension.buildingWidth / 2) * trussDimension.roofPitch / 12) / 3 * 2 + roofRailDistanceY;
    const bottomBaseRailOffset = (trussType != "HSST") ? 0 : 0.6

    const sideEdgeSlopeRailLength = (trussDimension.buildingWidth * 0.2 > 5) ? 5 : trussDimension.buildingWidth * 0.2;
    const sideEdgeSlopeRailAlpha = Math.PI / 4;
    const roofStylePosOffset = params.p_r_s === 1 ? 0.2 : 0;

    const sideEdgeSlopeRailPosY = useMemo(() => {
        return trussDimension.buildingHeight - roofRailDistanceY - (sideEdgeSlopeRailLength * Math.sin(sideEdgeSlopeRailAlpha) - (sideEdgeSlopeRailLength * Math.cos(sideEdgeSlopeRailAlpha)) * Math.tan(roofAlpha)) + roofStylePosOffset * 2  + bottomBaseRailHeight/2 - (params.p_r_s === 1 ? trussDimension.roofPitch / 60 : 0);
    }, [trussDimension.buildingHeight, roofAlpha, roofStylePosOffset, params.p_r_s, trussDimension.roofPitch,roofRailDistanceY,sideEdgeSlopeRailLength]);

    const leftDoorDataArr = useMemo(() => (const_var.newDoorsArray.filter((item) => (item.userData.wallName === 'c_b_l_w' || item.userData.wallName === 'c_b_l_s_w' || item.userData.wallName === 'L_L_R_S_W'))), [const_var.newDoorsArray])
    const rightDoorDataArr = useMemo(() => (const_var.newDoorsArray.filter((item) => (item.userData.wallName === 'c_b_r_w' || item.userData.wallName === 'c_b_r_s_w' || item.userData.wallName === 'R_L_L_S_W'))), [const_var.newDoorsArray]) 
    
    const roofCenterRailHeight = ((trussDimension.buildingWidth / 2) * trussDimension.roofPitch / 12) * hangRailDivision + roofRailDistanceY;

    const connectLongRailModelDataArr = useMemo(() => {
        let dataInfoArray = [];

        const distance = trussDimension.buildingWidth / 9;
        for(let i = 1; i < 3; i++) {
            const pos_x = distance * i;
            const verticalRailHeight = (((trussDimension.roofPitch / 12) * (trussDimension.buildingWidth / 3 - pos_x) + roofRailDistanceY - connectRailHeight / 2));
            
            const tempInfoObj = {
                pos_x: pos_x,
                verticalRailHeight: verticalRailHeight,
            }
    
            dataInfoArray.push(tempInfoObj);
        }
        

        return dataInfoArray
    }, [trussDimension.buildingWidth, roofHeight, trussDimension.roofPitch, roofRailDistanceY])

    const connectShortRailModelDataArr = useMemo(() => {
        let dataInfoArray = [];
        let count, distance;

        const initDistance = (trussType != "HSST") ? 1.8 : 2;
        const tempCount = Math.floor(trussDimension.buildingWidth / 3 / initDistance);
        const distanceDiveder_1 = (trussType != "HSST") ? 6 : 3;
        const distanceDiveder_2 = (trussType != "HSST") ? 3 : 6;
        if (params.p_d === tempCount * initDistance) {
            count = tempCount;
            distance = initDistance;
        } else if (params.p_d - tempCount * initDistance < initDistance / 2) {
            count = tempCount;
            distance = (trussDimension.buildingWidth / distanceDiveder_1) / count;
        } else {
            count = tempCount + 1;
            distance = (trussDimension.buildingWidth / distanceDiveder_1) / count;
        }

        let verticalRailHeight = 0;
        let connectorWidth = (trussType != "HSST") ? connectRailHeight : shortConnectRailHeight

        for (let i = 0; i < count; i++) {
            const pos_x = trussDimension.buildingWidth / distanceDiveder_2 + distance * i + connectorWidth/2;
            const pos_y = trussDimension.buildingHeight + roofHeight / 2 + trussDimension.roofPitch / 12 * (trussDimension.buildingWidth / 4 - pos_x) - roofRailDistanceY - connectRailHeight / 2 + 0.02;
            verticalRailHeight = roofRailDistanceY;
            
            const tempInfoObj = {
                pos_x: pos_x,
                pos_y: pos_y,
                height: verticalRailHeight,
            }

            dataInfoArray.push(tempInfoObj);
        }

        return {
                verticalRailHeight,
                dataInfoArray
            }
    }, [trussDimension.buildingHeight, trussDimension.buildingWidth, roofHeight, trussDimension.roofPitch, roofRailDistanceY])

    useEffect(() => {
        if (ref_centerHRail.current && ref_centerVRail.current && ref_underTruss.current && ref_shortConnectRail.current && ref_sideSlopeRail.current) {
            let index_all_1 = 0;
            new Array(2).fill('').forEach((_, index_dir) => {
                let offsetForDir = 1;
                if (index_dir === 1) offsetForDir = 1;
                else offsetForDir = -1;

                new Array(trussModelPosInfoArr.length).fill('').forEach((_, index_truss) => {
                    //----- roof inner rail-----
                    obj_underTruss.position.set(offsetForDir * (-trussDimension.buildingWidth / 2 + connectRailWidth / 4), trussDimension.buildingHeight - roofRailDistanceY - bottomBaseRailHeight / 2 + 0.005 + alignPosOffset, trussModelPosInfoArr[index_truss]);
                    obj_underTruss.rotation.y = offsetForDir * Math.PI / 2;
                    obj_underTruss.rotateOnAxis(vector, offsetForDir * roofAlpha);
                    //--------------------------

                    //----- bottom base rail-----
                    if(trussType == "HSST"){
                        obj_centerHRail.position.set(-bottomBaseRailLength / 2 - bottomBaseRailOffset/2, trussDimension.buildingHeight + roofHeight / divider - roofRailDistanceY + bottomBaseRailHeight / 2 + alignPosOffset, trussModelPosInfoArr[index_truss]);
                    }else{
                        obj_centerHRail.position.set(-bottomBaseRailLength / 2, trussDimension.buildingHeight + roofHeight / 3 - roofRailDistanceY - bottomBaseRailHeight / 2 + alignPosOffset, trussModelPosInfoArr[index_truss]);
                    }
                    obj_centerHRail.rotation.y = Math.PI / 2;
                    //---------------------

                    //----- roof center rail-----
                    obj_centerVRail.position.set(0, trussDimension.buildingHeight + roofHeight / 3 - roofRailDistanceY - bottomBaseRailHeight + alignPosOffset, trussModelPosInfoArr[index_truss]);
                    obj_centerVRail.rotation.x = -Math.PI / 2;

                    //----- side slope rail-----
                    obj_sideSlopeRail.position.set(offsetForDir * (trussDimension.buildingWidth / 2 - 0.1 - 0.05), sideEdgeSlopeRailPosY + alignPosOffset - bottomBaseRailHeight / 2, trussModelPosInfoArr[index_truss]);
                    obj_sideSlopeRail.rotation.y = offsetForDir * -Math.PI / 2;
                    obj_sideSlopeRail.rotateOnAxis(new THREE.Vector3(1, 0, 0), offsetForDir * (Math.PI / 2 - sideEdgeSlopeRailAlpha));

                    let overlap_left, overlap_right = false;
                    leftDoorDataArr.forEach((item) => {                            
                        if (item.userData.wallName === 'c_b_l_w' || item.userData.wallName === 'c_b_l_s_w') {
                            if (item.positionX - item.width / 2 < trussModelPosInfoArr[index_truss] + connectRailWidth / 2 &&
                                item.positionX + item.width / 2 >= trussModelPosInfoArr[index_truss] - connectRailWidth / 2 &&
                                item.positionY + item.height / 2 >= sideEdgeSlopeRailPosY
                            ) {
                                overlap_left = true;
                            }
                        } else if (item.userData.wallName === 'L_L_R_S_W') {
                            if (-item.positionX - item.width / 2 < trussModelPosInfoArr[index_truss] + connectRailWidth / 2 &&
                                -item.positionX + item.width / 2 >= trussModelPosInfoArr[index_truss] - connectRailWidth / 2 &&
                                item.positionY + item.height / 2 >= sideEdgeSlopeRailPosY
                            ) {
                                overlap_left = true;
                            }
                        }
                    })

                    rightDoorDataArr.forEach((item) => {                            
                        if (item.userData.wallName === 'c_b_r_w' || item.userData.wallName === 'c_b_r_s_w') {
                            if (-item.positionX - item.width / 2 < trussModelPosInfoArr[index_truss] + connectRailWidth / 2 &&
                                -item.positionX + item.width / 2 >= trussModelPosInfoArr[index_truss] - connectRailWidth / 2 &&
                                item.positionY + item.height / 2 >= sideEdgeSlopeRailPosY
                                ) {
                                    overlap_right = true;
                            }
                        } else if (item.userData.wallName === 'R_L_L_S_W') {
                            if (item.positionX - item.width / 2 < trussModelPosInfoArr[index_truss] + connectRailWidth / 2 &&
                                item.positionX + item.width / 2 >= trussModelPosInfoArr[index_truss] - connectRailWidth / 2 &&
                                item.positionY + item.height / 2 >= sideEdgeSlopeRailPosY
                                ) {
                                    overlap_right = true;
                            }
                        }
                    })

                    if ((offsetForDir === 1 && (overlap_right === true || (controls.isCheckActiveTab === 'doorwindow' && params.activeWall === 'c_b_l_s_r_w'))) || 
                        (offsetForDir === -1 && (overlap_left === true || (controls.isCheckActiveTab === 'doorwindow' && params.activeWall === 'c_b_r_s_l_w'))) ||
                        trussType != "HSST"
                    )
                    {
                        obj_sideSlopeRail.scale.setScalar(0);
                    } else {
                        obj_sideSlopeRail.scale.setScalar(1);
                    }
                    
                    obj_sideSlopeRail.updateMatrix();
                    ref_sideSlopeRail.current.setMatrixAt(index_all_1, obj_sideSlopeRail.matrix);

                    //---------------------
                    if (hiddenBaseRailIdArr.includes(index_truss)) {
                        obj_underTruss.scale.setScalar(0);
                        obj_centerHRail.scale.setScalar(0);
                        obj_centerVRail.scale.setScalar(0);
                    } else {
                        obj_underTruss.scale.setScalar(1);
                        obj_centerHRail.scale.setScalar(1);
                        obj_centerVRail.scale.setScalar(1);
                    }
                    if(trussType == "HSST") obj_centerVRail.scale.setScalar(0);
                    
                    obj_underTruss.updateMatrix();
                    ref_underTruss.current.setMatrixAt(index_all_1, obj_underTruss.matrix);
                    obj_centerVRail.updateMatrix();
                    ref_centerVRail.current.setMatrixAt(index_all_1, obj_centerVRail.matrix);
                    obj_centerHRail.updateMatrix();
                    ref_centerHRail.current.setMatrixAt(index_all_1, obj_centerHRail.matrix);
                    obj_sideSlopeRail.updateMatrix();
                    ref_sideSlopeRail.current.setMatrixAt(index_all_1, obj_sideSlopeRail.matrix);

                    index_all_1 ++;
                })
            })

            let index_all_3 = 0;
            new Array(2).fill('').forEach((_, index_dir) => {
                let offsetForDir = 1;
                if (index_dir === 0) offsetForDir = 1;
                else offsetForDir = -1;

                new Array(trussModelPosInfoArr.length).fill('').forEach((_, index_truss) => {
                    new Array(connectShortRailModelDataArr.dataInfoArray.length).fill('').forEach((_, index_rail) => {
                        obj_shortConnectRail.position.set(offsetForDir * connectShortRailModelDataArr.dataInfoArray[index_rail].pos_x, connectShortRailModelDataArr.dataInfoArray[index_rail].pos_y + alignPosOffset, trussModelPosInfoArr[index_truss]);
                        obj_shortConnectRail.rotation.x = -Math.PI / 2;
                        if(trussType == "HSST") obj_shortConnectRail.rotation.y = offsetForDir * (trussDimension.roofPitch/12)

                        if (hiddenBaseRailIdArr.includes(index_truss)) {
                            obj_shortConnectRail.scale.setScalar(0);
                        } else {
                            obj_shortConnectRail.scale.setScalar(1);
                        }

                        obj_shortConnectRail.updateMatrix();
                        ref_shortConnectRail.current.setMatrixAt(index_all_3, obj_shortConnectRail.matrix);

                        index_all_3 ++;
                    })
                })
            })
            
            ref_centerHRail.current.instanceMatrix.needsUpdate = true;
            ref_centerVRail.current.instanceMatrix.needsUpdate = true;
            ref_underTruss.current.instanceMatrix.needsUpdate = true;
            ref_shortConnectRail.current.instanceMatrix.needsUpdate = true;
            ref_sideSlopeRail.current.instanceMatrix.needsUpdate = true;
        }
    }, [ref_centerHRail.current, ref_centerVRail.current, ref_underTruss.current, ref_shortConnectRail.current, ref_sideSlopeRail.current, trussDimension.buildingWidth, trussDimension.buildingHeight, connectLongRailModelDataArr, connectShortRailModelDataArr, trussModelPosInfoArr, overlapTrussArr, hiddenBaseRailIdArr, roofHeight, alignPosOffset, roofAlpha, roofRailDistanceY])

    return (
        <group name='AST-truss-legs' rotation={[0,trussDimension.trussRotation,0]} position={[0, -offset, 0]} >
            <CommonTruss trussType={trussType} trussModelPosInfoArr={trussModelPosInfoArr} overlapTrussArr={overlapTrussArr} normalMap={normalMap} envMap={envMap} isShowSlopeRail={false} isFor={isFor} />
            <>
                {/* --- roof unnder truss model --- */}
                <instancedMesh ref={ref_underTruss} args={[null, null, trussModelPosInfoArr.length * 2]} frustumCulled={false}>
                    <extrudeGeometry args={[railModel(bottomBaseRailWidth, bottomBaseRailHeight), extrudeSettings(roofInnerRailLength - 0.01 )]} />
                    <MaterialTruss normalMap={normalMap} envMap={envMap} />
                </instancedMesh>
                {/* ------------------------- */}

                {/* --- bottom base rail model --- */}
                <instancedMesh   instancedMesh ref={ref_centerHRail} args={[null, null, trussModelPosInfoArr.length]} frustumCulled={false}>
                    <extrudeGeometry args={[railModel(bottomBaseRailWidth, (trussType != "HSST") ? bottomBaseRailHeight : bottomBaseRailHeight+0.05), extrudeSettings(bottomBaseRailLength + bottomBaseRailOffset)]} />
                    <MaterialTruss normalMap={normalMap} envMap={envMap} />
                </instancedMesh>
                {/* ------------------------- */}

                {/* --- roof center rail model --- */}
                <instancedMesh ref={ref_centerVRail} args={[null, null, trussModelPosInfoArr.length]} frustumCulled={false}>
                    <extrudeGeometry args={[railModel(connectRailHeight, connectRailWidth), extrudeSettings(roofCenterRailHeight)]} />
                    <MaterialTruss normalMap={normalMap} envMap={envMap} />
                </instancedMesh>
                {/* ------------------------- */}

                {/* --- connect rail model --- */}
                {(trussType != "HSST") && connectLongRailModelDataArr.map((_, index) => 
                    <VerticalLongConnectRailModel
                        index={index}
                        trussModelPosInfoArr={trussModelPosInfoArr}
                        connectLongRailModelDataArr={connectLongRailModelDataArr}
                        hiddenBaseRailIdArr={hiddenBaseRailIdArr}
                        roofHeight={roofHeight}
                        roofRailDistanceY={roofRailDistanceY}
                        bottomBaseRailHeight={bottomBaseRailHeight}
                        connectRailHeight={connectRailHeight}
                        connectRailWidth={connectRailWidth}
                        normalMap={normalMap}
                        envMap={envMap}
                        alignPosOffset={alignPosOffset}
                        isFor={isFor}
                    />
                )}

                {/* --- slope rail model --- */}
                <instancedMesh name='left_leg_right_leg_brace' ref={ref_sideSlopeRail} args={[null, null, trussModelPosInfoArr.length * 2]} frustumCulled={false}>
                    <extrudeGeometry args={[railModel(bottomBaseRailWidth, bottomBaseRailHeight), extrudeSettings(sideEdgeSlopeRailLength - 0.15)]} />
                    <meshPhongMaterial color={"#F0F0F0"} map={normalMap} envMap={envMap} side={THREE.DoubleSide} reflectivity={0.4} />
                </instancedMesh>
                {/* ------------------------- */}

                <instancedMesh ref={ref_shortConnectRail} args={[null, null, trussModelPosInfoArr.length * connectShortRailModelDataArr.dataInfoArray.length * 2]} frustumCulled={false}>
                    <extrudeGeometry args={[railModel((trussType != "HSST") ? connectRailHeight : shortConnectRailHeight, connectRailWidth), extrudeSettings(connectShortRailModelDataArr.verticalRailHeight)]} />
                    <MaterialTruss normalMap={normalMap} envMap={envMap} />
                </instancedMesh>
                {/* ------------------------- */}
            </>
        </group>
    )
}

const VerticalLongConnectRailModel = ({ index, trussModelPosInfoArr, connectLongRailModelDataArr, hiddenBaseRailIdArr, roofHeight, roofRailDistanceY, bottomBaseRailHeight, connectRailHeight, connectRailWidth, mainColor, normalMap, envMap, alignPosOffset, isFor }) => {
    const obj_longConnectRail = new THREE.Object3D();
    const ref_longConnectRail = useRef();
    const trussDimension = trussDimensions(isFor)

    const updateInstancedMesh = useCallback(() => {
        if (ref_longConnectRail.current) {
            let index_all = 0;
            new Array(2).fill('').forEach((_, index_dir) => {
                let offsetForDir = 1;
                if (index_dir === 0) offsetForDir = 1;
                else offsetForDir = -1;

                new Array(trussModelPosInfoArr.length).fill('').forEach((_, index_truss) => {
                    obj_longConnectRail.position.set(offsetForDir * connectLongRailModelDataArr[index].pos_x, trussDimension.buildingHeight + roofHeight / 3 - roofRailDistanceY - bottomBaseRailHeight + alignPosOffset, trussModelPosInfoArr[index_truss]);
                    obj_longConnectRail.rotation.x = -Math.PI / 2;

                    if (hiddenBaseRailIdArr.includes(index_truss)) {
                        obj_longConnectRail.scale.setScalar(0);
                    } else {
                        obj_longConnectRail.scale.setScalar(1);
                    }

                    obj_longConnectRail.updateMatrix();
                    ref_longConnectRail.current.setMatrixAt(index_all, obj_longConnectRail.matrix);

                    index_all ++;
                })
            })

            
            ref_longConnectRail.current.instanceMatrix.needsUpdate = true;
        }
    }, [ref_longConnectRail.current, trussDimension.buildingWidth, trussDimension.buildingHeight, params.singleSlope, connectLongRailModelDataArr, trussModelPosInfoArr, hiddenBaseRailIdArr, roofHeight, alignPosOffset, roofRailDistanceY])

    useLayoutEffect(() => {
        updateInstancedMesh();
    }, [updateInstancedMesh]);
    
    return (
        <instancedMesh ref={ref_longConnectRail} args={[null, null, trussModelPosInfoArr.length * 2]} frustumCulled={false}>
            <extrudeGeometry args={[railModel(connectRailHeight, connectRailWidth), extrudeSettings(connectLongRailModelDataArr[index].verticalRailHeight)]} />
            <MaterialTruss normalMap={normalMap} envMap={envMap} />
        </instancedMesh>
    )
}

// const PillarRailModel = ({ trussModelPosInfoArr, normalMap, envMap, meshDelModel_CSG, overlapTrussArr }) => {
//     const obj_leftOuterPillar = new THREE.Object3D();
//     const obj_leftInnerPillar = new THREE.Object3D();
//     const obj_leftOuterPillarHighlight = new THREE.Object3D();
//     const obj_leftConnectRail = new THREE.Object3D();
    
//     const obj_rightOuterPillar = new THREE.Object3D();
//     const obj_rightInnerPillar = new THREE.Object3D();
//     const obj_rightOuterPillarHighlight = new THREE.Object3D();
//     const obj_rightConnectRail = new THREE.Object3D();

//     const ref_leftOuterPillar = useRef();
//     const ref_leftInnerPillar = useRef();
//     const ref_leftConnectRail = useRef();    

//     const ref_rightOuterPillar = useRef();
//     const ref_rightInnerPillar = useRef();
//     const ref_rightConnectRail = useRef();    

//     const [csgLeftLegIdArr, setCsgLeftLegIdArr] = useState([])
//     const [csgRightLegIdArr, setCsgRightLegIdArr] = useState([])

//     const railWidth = 0.2;
//     const railHeight = 0.3;
//     const outInDistance = 0.5;

//     const alignPosOffset = useMemo(() => {
//         if(params.p_r_s === 1 ) return 0.32 + (trussDimension.roofPitch - 3) * 0.1 / 3;
//         else if(params.p_r_s === 2 || params.p_r_s === 3) return 0;
//         else return 0;
//     }, [params.p_r_s, trussDimension.roofPitch])
    
//     const connectRailDataArr = useMemo(() => {
//         let dataInfoArray = [];
//         let count, distance;

//         let initDistance = 2;
//         const tempCount = Math.floor(trussDimension.buildingHeight / initDistance);
//         if (trussDimension.buildingHeight === tempCount * initDistance) {
//             count = tempCount;
//             distance = initDistance;
//         } else if (trussDimension.buildingHeight - tempCount * initDistance <= initDistance / 2) {
//             count = tempCount;
//             distance = trussDimension.buildingHeight / count;
//         } else {
//             count = tempCount + 1;
//             distance = trussDimension.buildingHeight / count;
//         }

//         for (let i = 0; i < count; i++) {            
//             const pos_x = trussDimension.buildingWidth / 2 - railWidth / 2;
//             const pos_y = distance * i + railWidth / 2;
//             const railLength = outInDistance;

//             const tempInfoObj = {
//                 pos_x: pos_x,
//                 pos_y: pos_y,
//                 length: railLength,
//             }

//             dataInfoArray.push(tempInfoObj);
//         }

//         return {
//             dataInfoArray,
//             count
//         }
//     }, [trussDimension.buildingHeight, trussDimension.buildingWidth])

//     const connectRailLength = useMemo(() => {
//         let connectRailLength;

//         connectRailDataArr.dataInfoArray.map((item) => {
//             connectRailLength = item.length;
//         })
//         return connectRailLength
//     }, [connectRailDataArr])

//     const frontBackPillarRailDataArr = useMemo(() => {
//         let dataInfoArray = []
//         let endCrossBracingLegs = []
//         let endCrossBracingHorizonTruss = []
//         let endCrossBracingSlopeTruss = []
//         let count, distance = 0;
//         let horizonTrussCount = 0
        
//         const initDistance = 5;
//         const tempCount = Math.floor(trussDimension.buildingWidth / initDistance);
//         if (trussDimension.buildingWidth === tempCount * initDistance) {
//             count = tempCount;
//             distance = initDistance;
//         } else if (trussDimension.buildingWidth - tempCount * initDistance <= initDistance / 2) {
//             count = tempCount;
//             distance = trussDimension.buildingWidth / count;
//         } else {
//             count = tempCount + 1;
//             distance = trussDimension.buildingWidth / count;
//         }

//         const horizonTrussDistance = (distance / 2) / 4 * 5;
//         const tempHorizonTrussCount = Math.floor(trussDimension.buildingHeight / horizonTrussDistance)
//         const slopeTrussAngle = Math.atan((distance / 2) / (horizonTrussDistance + railHeight))
//         const slopeTrussLength = Math.sqrt(Math.pow(horizonTrussDistance, 2) + Math.pow((distance / 2), 2))

//         if (trussDimension.buildingHeight > horizonTrussDistance * tempHorizonTrussCount) {
//             horizonTrussCount = tempHorizonTrussCount + 1;
//         } else {
//             horizonTrussCount = tempHorizonTrussCount;
//         }
        
//         for (let i = 1; i < count; i++) {
//             const pos_x = distance * i - trussDimension.buildingWidth / 2;
//             const length = trussDimension.buildingHeight + (trussDimension.buildingWidth / 2 - Math.abs(pos_x)) * (trussDimension.roofPitch / 12);
            
//             const tempInfoObj ={
//                 pos_x: pos_x,
//                 length: length
//             }

//             dataInfoArray.push(tempInfoObj);
//         }

        
//         // front end cross bracing legs and horizon, slope truss
        
//         for(let i = 0; i < 2; i++) {
//             let pos_x;
            
//             const length = trussDimension.buildingHeight + (distance / 2) * (trussDimension.roofPitch / 12);
//             if(i === 0) {
//                 pos_x = -trussDimension.buildingWidth / 2 + distance / 2
//                 for(let j = 0; j < horizonTrussCount; j++) {
//                     let pos_y;
//                     pos_y = horizonTrussDistance * j
//                     const tempHorizonTrussInfoObj = {
//                         pos_x: pos_x,
//                         pos_y: pos_y + railWidth / 2,
//                         length: distance / 2
//                     }
//                     endCrossBracingHorizonTruss.push(tempHorizonTrussInfoObj)
//                 }
//                 for(let k = 0; k < horizonTrussCount - 1; k++) {
//                     let pos_y;
//                     pos_y = horizonTrussDistance * k
//                     const tempSlopeTrussInfoObj = {
//                         pos_x: pos_x,
//                         pos_y: pos_y,
//                         length: slopeTrussLength,
//                         rotationZ : slopeTrussAngle
//                     }
//                     endCrossBracingSlopeTruss.push(tempSlopeTrussInfoObj)
//                 }
//             } else if (i === 1) {
//                 let pos_y;
//                 pos_x = trussDimension.buildingWidth / 2 - distance / 2
//                 for(let j = 0; j < horizonTrussCount; j++) {
//                     pos_y = horizonTrussDistance * j
//                     const tempHorizonTrussInfoObj = {
//                         pos_x: pos_x + distance / 2,
//                         pos_y: pos_y + railWidth / 2,
//                         length: distance / 2
//                     }
//                     endCrossBracingHorizonTruss.push(tempHorizonTrussInfoObj)
                    
//                 }
//                 for(let k = 0; k < horizonTrussCount - 1; k++) {
//                     pos_y = horizonTrussDistance * k
//                     const tempSlopeTrussInfoObj = {
//                         pos_x: pos_x,
//                         pos_y: pos_y,
//                         length: slopeTrussLength,
//                         rotationZ : -slopeTrussAngle
//                     }
//                     endCrossBracingSlopeTruss.push(tempSlopeTrussInfoObj)
//                 }
//             }

//             const tempLegInfoObj ={
//                 pos_x: pos_x,
//                 length: length
//             }
            
            
//             endCrossBracingLegs.push(tempLegInfoObj)
//         }



//         return {
//             dataInfoArray,
//             endCrossBracingLegs,
//             endCrossBracingHorizonTruss,
//             endCrossBracingSlopeTruss
//         };

//     }, [trussDimension.buildingHeight, trussDimension.buildingWidth, trussDimension.roofPitch]) 

//     const sidePillarRailDataArr = useMemo(() => {
//         const legDistance = (const_var.DistanceArr.length>0 && const_var.DistanceArr[params.p_b_t][params.p_r_s]!=undefined) ? const_var.DistanceArr[params.p_b_t][params.p_r_s] : 5

//         let sideCrossBracingHorizonTruss = []
//         let sideCrossBracingSlopeTruss = []
//         let count, distance = 0;
//         let horizonTrussCount = 0
        
//         const initDistance = legDistance;
//         const tempCount = Math.floor(params.p_d / initDistance);
//         if (params.p_d === tempCount * initDistance) {
//             count = tempCount;
//             distance = initDistance;
//         } else if (params.p_d - tempCount * initDistance < initDistance / 2) {
//             count = tempCount;
//             distance = params.p_d / count;
//         } else {
//             count = tempCount + 1;
//             distance = params.p_d / count;
//         }
        
//         const horizonTrussDistance = (distance / 2) / 4 * 5;
//         const tempHorizonTrussCount = Math.floor(trussDimension.buildingHeight / horizonTrussDistance)
//         const slopeTrussAngle = Math.atan((distance) / (horizonTrussDistance + railHeight / 2))
//         const slopeTrussLength = Math.sqrt(Math.pow(horizonTrussDistance, 2) + Math.pow((distance), 2))

//         if (trussDimension.buildingHeight > horizonTrussDistance * tempHorizonTrussCount) {
//             horizonTrussCount = tempHorizonTrussCount + 1;
//         } else {
//             horizonTrussCount = tempHorizonTrussCount;
//         }
        
//         // front end cross bracing legs and horizon, slope truss
        
//         for(let i = 0; i < 2; i++) {
//             let pos_Z;
            
//             if(i === 0) {
//                 pos_Z = -params.p_d / 2
//                 for(let j = 1; j < horizonTrussCount; j++) {
//                     let pos_y;
//                     pos_y = horizonTrussDistance * j
//                     const tempHorizonTrussInfoObj = {
//                         pos_Z: pos_Z,
//                         pos_y: pos_y + railWidth / 2,
//                         length: distance
//                     }
//                     sideCrossBracingHorizonTruss.push(tempHorizonTrussInfoObj)
//                 }
//                 for(let k = 0; k < horizonTrussCount - 1; k++) {
//                     let pos_y;
//                     pos_y = horizonTrussDistance * k
//                     const tempSlopeTrussInfoObj = {
//                         pos_Z: pos_Z + distance,
//                         pos_y: pos_y,
//                         length: slopeTrussLength,
//                         rotationX : -(Math.PI / 2 + slopeTrussAngle)
//                     }
//                     sideCrossBracingSlopeTruss.push(tempSlopeTrussInfoObj)
//                 }
//             } else if (i === 1) {
//                 let pos_y;
//                 pos_Z = params.p_d / 2 - distance
//                 for(let j = 1; j < horizonTrussCount; j++) {
//                     pos_y = horizonTrussDistance * j
//                     const tempHorizonTrussInfoObj = {
//                         pos_Z: pos_Z,
//                         pos_y: pos_y + railWidth / 2,
//                         length: distance
//                     }
//                     sideCrossBracingHorizonTruss.push(tempHorizonTrussInfoObj)
                    
//                 }
//                 for(let k = 0; k < horizonTrussCount - 1; k++) {
//                     pos_y = horizonTrussDistance * k
//                     const tempSlopeTrussInfoObj = {
//                         pos_Z: pos_Z,
//                         pos_y: pos_y,
//                         length: slopeTrussLength,
//                         rotationX : -(Math.PI / 2 - slopeTrussAngle)
//                     }
//                     sideCrossBracingSlopeTruss.push(tempSlopeTrussInfoObj)
//                 }
//             }
            
//         }

//         return {
//             sideCrossBracingHorizonTruss,
//             sideCrossBracingSlopeTruss
//         };
//     }, [trussDimension.buildingHeight, params.p_d, trussDimension.roofPitch, const_var.DistanceArr, params.p_b_t, params.p_r_s,params.fourth_center_cost])

//     /************** cutting hole using CSG ***************/

//     const CSGFrontBackPillarRailModelArr = useMemo(() => {
//         let frontModelArr = [];
//         let backModelArr = [];
//         let frontEndCrossBracingLegsArr = [];
//         let frontEndCrossBracingHorizonTrussArr = [];
//         let frontEndCrossBracingSlopeTrussArr = [];
//         let backEndCrossBracingLegsArr = []
//         let backEndCrossBracingHorizonTrussArr = [];
//         let backEndCrossBracingSlopeTrussArr = []
        
//         const modelShape = railModel(railWidth, railHeight);
//         frontBackPillarRailDataArr.dataInfoArray.map((item) => {    
//             const meshPillarRailModel = new THREE.Mesh(
//                 new THREE.ExtrudeGeometry(modelShape, extrudeSettings(item.length - railHeight / 2)),
//                 new THREE.MeshStandardMaterial({ color: 'pink' })
//             )
        
//             let meshFrontPillarRailModel_Temp = meshPillarRailModel.clone();    
//             meshFrontPillarRailModel_Temp.position.set(item.pos_x, 0, 0);
//             meshFrontPillarRailModel_Temp.rotateX(-Math.PI / 2);
//             meshFrontPillarRailModel_Temp.updateMatrix();
//             meshFrontPillarRailModel_Temp.updateMatrixWorld();
//             const meshFrontPillarRailModel_CSG = CSG.fromMesh(meshFrontPillarRailModel_Temp);

//             let meshBackPillarRailModel_Temp = meshPillarRailModel.clone();    
//             meshBackPillarRailModel_Temp.position.set(item.pos_x, 0, 0);
//             meshBackPillarRailModel_Temp.rotateX(-Math.PI / 2);
//             meshBackPillarRailModel_Temp.updateMatrix();
//             meshBackPillarRailModel_Temp.updateMatrixWorld();
//             const meshBackPillarRailModel_CSG = CSG.fromMesh(meshBackPillarRailModel_Temp);

//             const frontPillarRailModel_CSG = meshFrontPillarRailModel_CSG.subtract(meshDelModel_CSG.front);
//             const backPillarRailModel_CSG = meshBackPillarRailModel_CSG.subtract(meshDelModel_CSG.back);

//             const CSGFrontPillarRailModel = CSG.toMesh(
//                 frontPillarRailModel_CSG,
//                 new THREE.Matrix4(),
//                 new THREE.MeshStandardMaterial({ color: 'blue' })
//             )
//             const CSGBackPillarRailModel = CSG.toMesh(
//                 backPillarRailModel_CSG,
//                 new THREE.Matrix4(),
//                 new THREE.MeshStandardMaterial({ color: 'blue' })
//             )
        
//             frontModelArr.push(CSGFrontPillarRailModel);
//             backModelArr.push(CSGBackPillarRailModel);

//             return null;
//         })

//         frontBackPillarRailDataArr.endCrossBracingLegs.map((item) => {    
//             const meshPillarRailModel = new THREE.Mesh(
//                 new THREE.ExtrudeGeometry(modelShape, extrudeSettings(item.length)),
//                 new THREE.MeshStandardMaterial({ color: 'pink' })
//             )
        
//             let meshFrontEndCrossBracingLegs = meshPillarRailModel.clone();    
//             meshFrontEndCrossBracingLegs.position.set(item.pos_x, 0, 0);
//             meshFrontEndCrossBracingLegs.rotateX(-Math.PI / 2);
//             meshFrontEndCrossBracingLegs.updateMatrix();
//             meshFrontEndCrossBracingLegs.updateMatrixWorld();
//             const meshFrontEndCrossBracingLegs_CSG = CSG.fromMesh(meshFrontEndCrossBracingLegs);
//             const meshBackEndCrossBracingLegs_CSG = CSG.fromMesh(meshFrontEndCrossBracingLegs);

//             const frontEndCrossBracingLegs_CSG = meshFrontEndCrossBracingLegs_CSG.subtract(meshDelModel_CSG.front);
//             const backEndCrossBracingLegs_CSG = meshBackEndCrossBracingLegs_CSG.subtract(meshDelModel_CSG.back);
            
//             const CSGFrontEndCrossBracingLegs = CSG.toMesh(
//                 frontEndCrossBracingLegs_CSG,
//                 new THREE.Matrix4(),
//                 new THREE.MeshStandardMaterial({ color: 'blue' })
//             )
//             const CSGBackEndCrossBracingLegs = CSG.toMesh(
//                 backEndCrossBracingLegs_CSG,
//                 new THREE.Matrix4(),
//                 new THREE.MeshStandardMaterial({ color: 'blue' })
//             )
        
//             frontEndCrossBracingLegsArr.push(CSGFrontEndCrossBracingLegs);
//             backEndCrossBracingLegsArr.push(CSGBackEndCrossBracingLegs);
            

//             return null;
//         })

//         frontBackPillarRailDataArr.endCrossBracingHorizonTruss.map((item) => {    
//             const meshPillarRailModel = new THREE.Mesh(
//                 new THREE.ExtrudeGeometry(modelShape, extrudeSettings(item.length)),
//                 new THREE.MeshStandardMaterial({ color: 'pink' })
//             )
        
//             let meshFrontEndCrossBracingHorizonTruss = meshPillarRailModel.clone();    
//             meshFrontEndCrossBracingHorizonTruss.position.set(item.pos_x, item.pos_y, 0);
//             meshFrontEndCrossBracingHorizonTruss.rotateY(-Math.PI / 2);
//             meshFrontEndCrossBracingHorizonTruss.rotateZ(Math.PI / 2);
//             meshFrontEndCrossBracingHorizonTruss.updateMatrix();
//             meshFrontEndCrossBracingHorizonTruss.updateMatrixWorld();
//             const meshFrontEndCrossBracingHorizonTruss_CSG = CSG.fromMesh(meshFrontEndCrossBracingHorizonTruss);
//             const meshBackEndCrossBracingHorizonTruss_CSG = CSG.fromMesh(meshFrontEndCrossBracingHorizonTruss);

//             const frontEndCrossBracingHorizonTruss_CSG = meshFrontEndCrossBracingHorizonTruss_CSG.subtract(meshDelModel_CSG.front);
//             const backEndCrossBracingHorizonTruss_CSG = meshBackEndCrossBracingHorizonTruss_CSG.subtract(meshDelModel_CSG.back);

//             const CSGFrontEndCrossBracingHorizonTruss = CSG.toMesh(
//                 frontEndCrossBracingHorizonTruss_CSG,
//                 new THREE.Matrix4(),
//                 new THREE.MeshStandardMaterial({ color: 'blue' })
//             )
//             const CSGBackEndCrossBracingHorizonTruss = CSG.toMesh(
//                 backEndCrossBracingHorizonTruss_CSG,
//                 new THREE.Matrix4(),
//                 new THREE.MeshStandardMaterial({ color: 'blue' })
//             )
        
//             frontEndCrossBracingHorizonTrussArr.push(CSGFrontEndCrossBracingHorizonTruss);
//             backEndCrossBracingHorizonTrussArr.push(CSGBackEndCrossBracingHorizonTruss);

//             return null;
//         })
//         frontBackPillarRailDataArr.endCrossBracingSlopeTruss.map((item) => {    
//             const meshPillarRailModel = new THREE.Mesh(
//                 new THREE.ExtrudeGeometry(modelShape, extrudeSettings(item.length)),
//                 new THREE.MeshStandardMaterial({ color: 'pink' })
//             )
        
//             let meshFrontEndCrossBracingSlopeTruss = meshPillarRailModel.clone();    
//             meshFrontEndCrossBracingSlopeTruss.position.set(item.pos_x, item.pos_y, 0);
//             meshFrontEndCrossBracingSlopeTruss.rotateX(-Math.PI / 2);
//             meshFrontEndCrossBracingSlopeTruss.rotateY(-item.rotationZ);

//             meshFrontEndCrossBracingSlopeTruss.updateMatrix();
//             meshFrontEndCrossBracingSlopeTruss.updateMatrixWorld();
//             const meshFrontEndCrossBracingSlopeTruss_CSG = CSG.fromMesh(meshFrontEndCrossBracingSlopeTruss);
//             const meshBackEndCrossBracingSlopeTruss_CSG = CSG.fromMesh(meshFrontEndCrossBracingSlopeTruss);

//             const frontEndCrossBracingSlopeTruss_CSG = meshFrontEndCrossBracingSlopeTruss_CSG.subtract(meshDelModel_CSG.front);
//             const backEndCrossBracingSlopeTruss_CSG = meshBackEndCrossBracingSlopeTruss_CSG.subtract(meshDelModel_CSG.back);

//             const CSGFrontEndCrossBracingSlopeTruss = CSG.toMesh(
//                 frontEndCrossBracingSlopeTruss_CSG,
//                 new THREE.Matrix4(),
//                 new THREE.MeshStandardMaterial({ color: 'blue' })
//             )
//             const CSGBackEndCrossBracingSlopeTruss = CSG.toMesh(
//                 backEndCrossBracingSlopeTruss_CSG,
//                 new THREE.Matrix4(),
//                 new THREE.MeshStandardMaterial({ color: 'blue' })
//             )
        
//             frontEndCrossBracingSlopeTrussArr.push(CSGFrontEndCrossBracingSlopeTruss);
//             backEndCrossBracingSlopeTrussArr.push(CSGBackEndCrossBracingSlopeTruss);

//             return null;
//         })
        
//         return { frontModelArr, backModelArr, frontEndCrossBracingLegsArr, frontEndCrossBracingHorizonTrussArr, frontEndCrossBracingSlopeTrussArr, backEndCrossBracingLegsArr, backEndCrossBracingHorizonTrussArr, backEndCrossBracingSlopeTrussArr, };
//     }, [frontBackPillarRailDataArr, meshDelModel_CSG.back, meshDelModel_CSG.front])

//     const CSGFrontBackBottomRailData = useMemo(() => {
//         const modelShape = railModel(railHeight, railWidth);

//         const meshBottomRailModel = new THREE.Mesh(
//             new THREE.ExtrudeGeometry(modelShape, extrudeSettings(trussDimension.buildingWidth)),
//             new THREE.MeshStandardMaterial({ color: 'pink' })
//         )
        
//         let meshFrontBottomRailModel_Temp = meshBottomRailModel.clone();    
//         meshFrontBottomRailModel_Temp.position.set(-trussDimension.buildingWidth / 2, railWidth / 2, 0);
//         meshFrontBottomRailModel_Temp.rotateY(Math.PI / 2);
//         meshFrontBottomRailModel_Temp.updateMatrix();
//         meshFrontBottomRailModel_Temp.updateMatrixWorld();
//         const meshFrontBottomRailModel_CSG = CSG.fromMesh(meshFrontBottomRailModel_Temp);

//         let meshBackBottomRailModel_Temp = meshBottomRailModel.clone();    
//         meshBackBottomRailModel_Temp.position.set(-trussDimension.buildingWidth / 2, railWidth / 2, 0);
//         meshBackBottomRailModel_Temp.rotateY(Math.PI / 2);
//         meshBackBottomRailModel_Temp.updateMatrix();
//         meshBackBottomRailModel_Temp.updateMatrixWorld();
//         const meshBackBottomRailModel_CSG = CSG.fromMesh(meshBackBottomRailModel_Temp);

//         const frontBottomRailModel_CSG = meshFrontBottomRailModel_CSG.subtract(meshDelModel_CSG.front);
//         const backBottomRailModel_CSG = meshBackBottomRailModel_CSG.subtract(meshDelModel_CSG.back);

//         const CSGFrontBottomRailModel = CSG.toMesh(
//             frontBottomRailModel_CSG,
//             new THREE.Matrix4(),
//             new THREE.MeshStandardMaterial({ color: 'blue' })
//         )
//         CSGFrontBottomRailModel.rotateY(Math.PI / 2);
//         CSGFrontBottomRailModel.position.set(-trussDimension.buildingWidth / 2, railWidth / 2, 0)

//         const CSGBackBottomRailModel = CSG.toMesh(
//             backBottomRailModel_CSG,
//             new THREE.Matrix4(),
//             new THREE.MeshStandardMaterial({ color: 'blue' })
//         )

//         return { CSGFrontBottomRailModel, CSGBackBottomRailModel };
//     }, [trussDimension.buildingWidth, meshDelModel_CSG.back, meshDelModel_CSG.front])

//     const CSGSideRailGroupModelArr = useMemo(() => {
//         let leftModelArr = [];
//         let leftConnectModelArr = [];
//         let rightModelArr = [];
//         let rightConnectModelArr = [];
//         let leftSideCrossBracingHorizonTrussArr = [];
//         let rightSideCrossBracingHorizonTrussArr = [];
//         let leftSideCrossBracingSlopeTrussArr = [];
//         let rightSideCrossBracingSlopeTrussArr = [];
        
//         const modelShape = railModel(railWidth, railHeight);
//         const modelShapeConnect = railModel(railHeight, railWidth);
//         trussModelPosInfoArr.map((pos_z) => {    
//             const meshOuterPillarRailModel = new THREE.Mesh(
//                 new THREE.ExtrudeGeometry(modelShape, extrudeSettings(trussDimension.buildingHeight)),
//                 new THREE.MeshStandardMaterial({ color: 'pink' })
//             )
//             const meshInnerPillarRailModel = new THREE.Mesh(
//                 new THREE.ExtrudeGeometry(modelShape, extrudeSettings(trussDimension.buildingHeight + alignPosOffset)),
//                 new THREE.MeshStandardMaterial({ color: 'pink' })
//             )

//             //--- left -- outer
        
//             let meshLeftOuterPillarRailModel_Temp = meshOuterPillarRailModel.clone();    
//             meshLeftOuterPillarRailModel_Temp.position.set(0, 0, pos_z);
//             meshLeftOuterPillarRailModel_Temp.rotateX(-Math.PI / 2);
//             meshLeftOuterPillarRailModel_Temp.updateMatrix();
//             meshLeftOuterPillarRailModel_Temp.updateMatrixWorld();
//             const meshLeftOuterPillarRailModel_CSG = CSG.fromMesh(meshLeftOuterPillarRailModel_Temp);

//             const leftOuterPillarRailModel_CSG = meshLeftOuterPillarRailModel_CSG.subtract(meshDelModel_CSG.left);
//             const CSGLeftOuterPillarRailModel = CSG.toMesh(
//                 leftOuterPillarRailModel_CSG,
//                 new THREE.Matrix4(),
//                 new THREE.MeshStandardMaterial({ color: 'blue' })
//             )

//             //--- left -- inner

//             let meshLeftInnerPillarRailModel_Temp = meshInnerPillarRailModel.clone();    
//             meshLeftInnerPillarRailModel_Temp.position.set(0, 0, pos_z);
//             meshLeftInnerPillarRailModel_Temp.rotateX(-Math.PI / 2);
//             meshLeftInnerPillarRailModel_Temp.updateMatrix();
//             meshLeftInnerPillarRailModel_Temp.updateMatrixWorld();
//             const meshLeftInnerPillarRailModel_CSG = CSG.fromMesh(meshLeftInnerPillarRailModel_Temp);

//             const leftInnerPillarRailModel_CSG = meshLeftInnerPillarRailModel_CSG.subtract(meshDelModel_CSG.left);
//             const CSGLeftInnerPillarRailModel = CSG.toMesh(
//                 leftInnerPillarRailModel_CSG,
//                 new THREE.Matrix4(),
//                 new THREE.MeshStandardMaterial({ color: 'blue' })
//             )

//             connectRailDataArr.dataInfoArray.map((item_connect) => {
//                 const meshLeftConnectRailModel = new THREE.Mesh(
//                     new THREE.ExtrudeGeometry(modelShapeConnect, extrudeSettings(item_connect.length)),
//                     new THREE.MeshStandardMaterial({ color: 'pink' })
//                 )
//                 let meshLeftConnectRailModel_Temp = meshLeftConnectRailModel.clone();    
//                 meshLeftConnectRailModel_Temp.position.set(0, item_connect.pos_y, pos_z);
//                 meshLeftConnectRailModel_Temp.rotateY(Math.PI / 2);
//                 meshLeftConnectRailModel_Temp.updateMatrix();
//                 meshLeftConnectRailModel_Temp.updateMatrixWorld();
//                 const meshLeftConnectRailModel_CSG = CSG.fromMesh(meshLeftConnectRailModel_Temp);
    
//                 const leftConnectRailModel_CSG = meshLeftConnectRailModel_CSG.subtract(meshDelModel_CSG.left);
//                 const CSGLeftConnectRailModel = CSG.toMesh(
//                     leftConnectRailModel_CSG,
//                     new THREE.Matrix4(),
//                     new THREE.MeshStandardMaterial({ color: 'blue' })
//                 )
                
//                 leftConnectModelArr.push(CSGLeftConnectRailModel);
                
//                 return null;
//             })

//             //--- right -- outer

//             let meshRightOuterPillarRailModel_Temp = meshOuterPillarRailModel.clone();    
//             meshRightOuterPillarRailModel_Temp.position.set(0, 0, pos_z);
//             meshRightOuterPillarRailModel_Temp.rotateX(-Math.PI / 2);
//             meshRightOuterPillarRailModel_Temp.updateMatrix();
//             meshRightOuterPillarRailModel_Temp.updateMatrixWorld();
//             const meshRightOuterPillarRailModel_CSG = CSG.fromMesh(meshRightOuterPillarRailModel_Temp);

//             const rightOuterPillarRailModel_CSG = meshRightOuterPillarRailModel_CSG.subtract(meshDelModel_CSG.right);
//             const CSGRightOuterPillarRailModel = CSG.toMesh(
//                 rightOuterPillarRailModel_CSG,
//                 new THREE.Matrix4(),
//                 new THREE.MeshStandardMaterial({ color: 'blue' })
//             )

//             //--- right -- inner

//             let meshRightInnerPillarRailModel_Temp = meshInnerPillarRailModel.clone();    
//             meshRightInnerPillarRailModel_Temp.position.set(0, 0, pos_z);
//             meshRightInnerPillarRailModel_Temp.rotateX(-Math.PI / 2);
//             meshRightInnerPillarRailModel_Temp.updateMatrix();
//             meshRightInnerPillarRailModel_Temp.updateMatrixWorld();
//             const meshRightInnerPillarRailModel_CSG = CSG.fromMesh(meshRightInnerPillarRailModel_Temp);

//             const rightInnerPillarRailModel_CSG = meshRightInnerPillarRailModel_CSG.subtract(meshDelModel_CSG.right);
//             const CSGRightInnerPillarRailModel = CSG.toMesh(
//                 rightInnerPillarRailModel_CSG,
//                 new THREE.Matrix4(),
//                 new THREE.MeshStandardMaterial({ color: 'blue' })
//             )

//             connectRailDataArr.dataInfoArray.map((item_connect) => {
//                 const meshRightConnectRailModel = new THREE.Mesh(
//                     new THREE.ExtrudeGeometry(modelShapeConnect, extrudeSettings(item_connect.length)),
//                     new THREE.MeshStandardMaterial({ color: 'pink' })
//                 )
//                 let meshRightConnectRailModel_Temp = meshRightConnectRailModel.clone();    
//                 meshRightConnectRailModel_Temp.position.set(0, item_connect.pos_y, pos_z);
//                 meshRightConnectRailModel_Temp.rotateY(-Math.PI / 2);
//                 meshRightConnectRailModel_Temp.updateMatrix();
//                 meshRightConnectRailModel_Temp.updateMatrixWorld();
//                 const meshRightConnectRailModel_CSG = CSG.fromMesh(meshRightConnectRailModel_Temp);
    
//                 const rightConnectRailModel_CSG = meshRightConnectRailModel_CSG.subtract(meshDelModel_CSG.right);
//                 const CSGRightConnectRailModel = CSG.toMesh(
//                     rightConnectRailModel_CSG,
//                     new THREE.Matrix4(),
//                     new THREE.MeshStandardMaterial({ color: 'blue' })
//                 )
                
//                 rightConnectModelArr.push(CSGRightConnectRailModel);
                
//                 return null;
//             })
        
//             leftModelArr.push({
//                     outer: CSGLeftOuterPillarRailModel,
//                     inner: CSGLeftInnerPillarRailModel
//                 });
//             rightModelArr.push({
//                     outer: CSGRightOuterPillarRailModel,
//                     inner: CSGRightInnerPillarRailModel
//                 });

//             return null;
//         })

//         sidePillarRailDataArr.sideCrossBracingHorizonTruss.map((item) => {    
//             const meshPillarRailModel = new THREE.Mesh(
//                 new THREE.ExtrudeGeometry(modelShape, extrudeSettings(item.length)),
//                 new THREE.MeshStandardMaterial({ color: 'pink' })
//             )
        
//             let meshLeftSideCrossBracingHorizonTruss = meshPillarRailModel.clone();    
//             meshLeftSideCrossBracingHorizonTruss.position.set(0, item.pos_y, item.pos_Z);
//             meshLeftSideCrossBracingHorizonTruss.updateMatrix();
//             meshLeftSideCrossBracingHorizonTruss.updateMatrixWorld();
//             const meshLeftSideCrossBracingHorizonTruss_CSG = CSG.fromMesh(meshLeftSideCrossBracingHorizonTruss);
//             const meshRightSideCrossBracingHorizonTruss_CSG = CSG.fromMesh(meshLeftSideCrossBracingHorizonTruss);

//             const leftSideCrossBracingHorizonTruss_CSG = meshLeftSideCrossBracingHorizonTruss_CSG.subtract(meshDelModel_CSG.left);
//             const rightSideCrossBracingHorizonTruss_CSG = meshRightSideCrossBracingHorizonTruss_CSG.subtract(meshDelModel_CSG.right);

//             const CSGLeftSideCrossBracingHorizonTruss = CSG.toMesh(
//                 leftSideCrossBracingHorizonTruss_CSG,
//                 new THREE.Matrix4(),
//                 new THREE.MeshStandardMaterial({ color: 'blue' })
//             )
//             const CSGRightSIdeCrossBracingHorizonTruss = CSG.toMesh(
//                 rightSideCrossBracingHorizonTruss_CSG,
//                 new THREE.Matrix4(),
//                 new THREE.MeshStandardMaterial({ color: 'blue' })
//             )
        
//             leftSideCrossBracingHorizonTrussArr.push(CSGLeftSideCrossBracingHorizonTruss);
//             rightSideCrossBracingHorizonTrussArr.push(CSGRightSIdeCrossBracingHorizonTruss);

//             return null;
//         })

//         sidePillarRailDataArr.sideCrossBracingSlopeTruss.map((item) => {    
//             const meshPillarRailModel = new THREE.Mesh(
//                 new THREE.ExtrudeGeometry(modelShape, extrudeSettings(item.length)),
//                 new THREE.MeshStandardMaterial({ color: 'pink' })
//             )
        
//             let meshLeftSideCrossBracingSlopeTruss = meshPillarRailModel.clone();    
//             meshLeftSideCrossBracingSlopeTruss.position.set(0, item.pos_y, item.pos_Z);
//             meshLeftSideCrossBracingSlopeTruss.rotateX(item.rotationX);
//             meshLeftSideCrossBracingSlopeTruss.updateMatrix();
//             meshLeftSideCrossBracingSlopeTruss.updateMatrixWorld();
//             const meshLeftSideCrossBracingSlopeTruss_CSG = CSG.fromMesh(meshLeftSideCrossBracingSlopeTruss);
//             const meshRightSideCrossBracingSlopeTruss_CSG = CSG.fromMesh(meshLeftSideCrossBracingSlopeTruss);

//             const leftSideCrossBracingSlopeTruss_CSG = meshLeftSideCrossBracingSlopeTruss_CSG.subtract(meshDelModel_CSG.left);
//             const rightSideCrossBracingSlopeTruss_CSG = meshRightSideCrossBracingSlopeTruss_CSG.subtract(meshDelModel_CSG.right);

//             const CSGLeftSideCrossBracingSlopeTruss = CSG.toMesh(
//                 leftSideCrossBracingSlopeTruss_CSG,
//                 new THREE.Matrix4(),
//                 new THREE.MeshStandardMaterial({ color: 'blue' })
//             )
//             const CSGRightSIdeCrossBracingSlopeTruss = CSG.toMesh(
//                 rightSideCrossBracingSlopeTruss_CSG,
//                 new THREE.Matrix4(),
//                 new THREE.MeshStandardMaterial({ color: 'blue' })
//             )
        
//             leftSideCrossBracingSlopeTrussArr.push(CSGLeftSideCrossBracingSlopeTruss);
//             rightSideCrossBracingSlopeTrussArr.push(CSGRightSIdeCrossBracingSlopeTruss);

//             return null;
//         })
        
//         return {
//             leftModelArr,
//             leftConnectModelArr,
//             rightModelArr,
//             rightConnectModelArr,
//             leftSideCrossBracingHorizonTrussArr,
//             rightSideCrossBracingHorizonTrussArr,
//             leftSideCrossBracingSlopeTrussArr,
//             rightSideCrossBracingSlopeTrussArr
//         };
//     }, [trussDimension.buildingHeight, connectRailDataArr, meshDelModel_CSG.left, meshDelModel_CSG.right, trussModelPosInfoArr, alignPosOffset])

//     /****************************************** *********/

//     useEffect(() => {
//         if (ref_leftOuterPillar.current && ref_leftInnerPillar.current && ref_rightOuterPillar.current && ref_rightInnerPillar.current && ref_leftConnectRail.current && ref_rightConnectRail.current) {
//             let isShowLeftLeg = true;
//             let isShowRightLeg = true;
//             let tempLeftCSGLegIdArr = [];
//             let tempRightCSGLegIdArr = [];
            
//             const leftSideCmpArry = [...const_var.newDoorsArray.filter((item) => wallNameArrForValid.find(subArray => subArray.includes('c_b_l_w')).includes(item.userData.wallName))];
//             const rightSideCmpArry = [...const_var.newDoorsArray.filter((item) => wallNameArrForValid.find(subArray => subArray.includes('c_b_r_w')).includes(item.userData.wallName))];

//             let index_cnt_all = 0;
//             trussModelPosInfoArr.forEach((pos_z, index_truss) => {
//                 //--- left outer leg
//                 obj_leftOuterPillar.position.set(-(trussDimension.buildingWidth / 2 - railWidth / 2), 0, pos_z);
//                 obj_leftOuterPillar.rotation.x = -Math.PI / 2;
//                 obj_leftInnerPillar.position.set(-(trussDimension.buildingWidth / 2 - railWidth / 2 - outInDistance), 0, pos_z);
//                 obj_leftInnerPillar.rotation.x = -Math.PI / 2;
                
//                 isShowLeftLeg = true
//                 leftSideCmpArry.every(element => {
//                     if (pos_z + railHeight >= element.positionX - element.width / 2 && pos_z - railHeight <= element.positionX + element.width / 2) {
//                         isShowLeftLeg = false;
//                         tempLeftCSGLegIdArr.push(index_truss);
//                         return false;
//                     }
//                     else return true;
//                 })
//                 if (!isShowLeftLeg || (!!overlapTrussArr && overlapTrussArr.buildingType === 'center' && overlapTrussArr.idArr.includes(index_truss))) {
//                     obj_leftOuterPillar.scale.setScalar(0);
//                 } else {
//                     obj_leftOuterPillar.scale.setScalar(1);
//                 }
//                 if (!isShowLeftLeg) {
//                     obj_leftInnerPillar.scale.setScalar(0);
//                 } else {
//                     obj_leftInnerPillar.scale.setScalar(1);
//                 }
//                 //----------

//                 //--- right outer leg
//                 obj_rightOuterPillar.position.set((trussDimension.buildingWidth / 2 - railWidth / 2), 0, pos_z);
//                 obj_rightOuterPillar.rotation.x = -Math.PI / 2;
//                 obj_rightInnerPillar.position.set(trussDimension.buildingWidth / 2 - railWidth / 2 - outInDistance, 0, pos_z);
//                 obj_rightInnerPillar.rotation.x = -Math.PI / 2;

//                 isShowRightLeg = true
//                 rightSideCmpArry.every(element => {
//                     if (pos_z + railHeight >= (-element.positionX) - element.width / 2 && pos_z - railHeight <= (-element.positionX) + element.width / 2) {
//                         isShowRightLeg = false;
//                         tempRightCSGLegIdArr.push(index_truss);
//                         return false;
//                     }
//                     else return true;
//                 })
//                 if (!isShowRightLeg || (!!overlapTrussArr && overlapTrussArr.buildingType === 'center' && overlapTrussArr.idArr.includes(index_truss))) {
//                     obj_rightOuterPillar.scale.setScalar(0);
//                 } else {
//                     obj_rightOuterPillar.scale.setScalar(1);
//                 }
//                 if (!isShowRightLeg) {
//                     obj_rightInnerPillar.scale.setScalar(0);
//                 } else {
//                     obj_rightInnerPillar.scale.setScalar(1);
//                 }
//                 //----------

//                 //--- connect leg
//                 connectRailDataArr.dataInfoArray.forEach((item, index_cnt) => {
//                     if (index_cnt === 0)  return;

//                     obj_leftConnectRail.position.set(-(trussDimension.buildingWidth / 2 - railWidth / 2), item.pos_y, pos_z);
//                     obj_leftConnectRail.rotation.y = Math.PI / 2;
//                     obj_leftConnectRail.scale.setScalar(1);

//                     obj_rightConnectRail.position.set((trussDimension.buildingWidth / 2 - railWidth / 2), item.pos_y, pos_z);
//                     obj_rightConnectRail.rotation.y = -Math.PI / 2;
//                     obj_rightConnectRail.scale.setScalar(1);

//                     leftSideCmpArry.forEach((element) => {
//                         if (pos_z + railHeight >= (element.positionX) - element.width / 2 && pos_z - railHeight <= (element.positionX) + element.width / 2) {
//                             if (item.pos_y - railWidth / 2 <= element.height) {
//                                 obj_leftConnectRail.scale.setScalar(0);
//                             }
//                         }
//                     })
//                     rightSideCmpArry.forEach((element) => {
//                         if (pos_z + railHeight >= (-element.positionX) - element.width / 2 && pos_z - railHeight <= (-element.positionX) + element.width / 2) {
//                             if (item.pos_y - railWidth / 2 <= element.height) {
//                                 obj_rightConnectRail.scale.setScalar(0);
//                             }
//                         }
//                     })
                    
//                     obj_leftConnectRail.updateMatrix();
//                     ref_leftConnectRail.current.setMatrixAt(index_cnt_all, obj_leftConnectRail.matrix);
//                     obj_rightConnectRail.updateMatrix();
//                     ref_rightConnectRail.current.setMatrixAt(index_cnt_all, obj_rightConnectRail.matrix);

//                     index_cnt_all ++;
//                 })

//                 obj_leftOuterPillar.updateMatrix();
//                 ref_leftOuterPillar.current.setMatrixAt(index_truss, obj_leftOuterPillar.matrix);
//                 obj_leftInnerPillar.updateMatrix();
//                 ref_leftInnerPillar.current.setMatrixAt(index_truss, obj_leftInnerPillar.matrix);
//                 obj_rightOuterPillar.updateMatrix();
//                 ref_rightOuterPillar.current.setMatrixAt(index_truss, obj_rightOuterPillar.matrix);
//                 obj_rightInnerPillar.updateMatrix();
//                 ref_rightInnerPillar.current.setMatrixAt(index_truss, obj_rightInnerPillar.matrix);
//             })

//             setCsgLeftLegIdArr(tempLeftCSGLegIdArr)
//             setCsgRightLegIdArr(tempRightCSGLegIdArr)

//             ref_leftOuterPillar.current.instanceMatrix.needsUpdate = true;
//             ref_leftInnerPillar.current.instanceMatrix.needsUpdate = true;
//             ref_rightOuterPillar.current.instanceMatrix.needsUpdate = true;
//             ref_rightInnerPillar.current.instanceMatrix.needsUpdate = true;

//             ref_leftConnectRail.current.instanceMatrix.needsUpdate = true;
//             ref_rightConnectRail.current.instanceMatrix.needsUpdate = true;
//         }
//     }, [ref_leftOuterPillar.current, ref_leftInnerPillar.current, ref_rightOuterPillar.current, ref_rightInnerPillar.current, ref_leftConnectRail.current, ref_rightConnectRail.current, trussDimension.buildingWidth, const_var.newDoorsArray, outInDistance, trussModelPosInfoArr, overlapTrussArr, connectRailDataArr])

//     //*************** instancing - highlight leg ********************/
//     const outerPillarHighlight = useMemo(() => {
//         if (overlapTrussArr.idArr?.length > 0) {
//             const instanceMatLeft = [];
//             const instanceMatRight = [];
//             trussModelPosInfoArr.forEach((pos_z, index_truss) => {
//                 obj_leftOuterPillarHighlight.position.set(-(trussDimension.buildingWidth / 2 - railWidth / 2) + 0.055, 0, pos_z);
//                 obj_leftOuterPillarHighlight.rotation.x = -Math.PI / 2;
//                 obj_rightOuterPillarHighlight.position.set((trussDimension.buildingWidth / 2 - railWidth / 2) - 0.055, 0, pos_z);
//                 obj_rightOuterPillarHighlight.rotation.x = -Math.PI / 2;

//                 if ((!!overlapTrussArr && overlapTrussArr.buildingType === 'center' && overlapTrussArr.idArr.includes(index_truss)) && !csgLeftLegIdArr.includes(index_truss)) {
//                     obj_leftOuterPillarHighlight.scale.setScalar(1);
//                 } else {
//                     obj_leftOuterPillarHighlight.scale.setScalar(0);
//                 }
//                 if ((!!overlapTrussArr && overlapTrussArr.buildingType === 'center' && overlapTrussArr.idArr.includes(index_truss)) && !csgRightLegIdArr.includes(index_truss)) {
//                     obj_rightOuterPillarHighlight.scale.setScalar(1);
//                 } else {
//                     obj_rightOuterPillarHighlight.scale.setScalar(0);
//                 }

//                 obj_leftOuterPillarHighlight.updateMatrix();
//                 instanceMatLeft.push(...obj_leftOuterPillarHighlight.matrix.elements);
//                 obj_rightOuterPillarHighlight.updateMatrix();
//                 instanceMatRight.push(...obj_rightOuterPillarHighlight.matrix.elements);
//             })

//             return {ref_leftOuterPillarHighlight: new THREE.InstancedBufferAttribute(new Float32Array(instanceMatLeft), 16), 
//                 ref_rightOuterPillarHighlight: new THREE.InstancedBufferAttribute(new Float32Array(instanceMatRight), 16)
//             };
//         }
//     }, [overlapTrussArr.idArr, trussDimension.buildingWidth, trussModelPosInfoArr, overlapTrussArr])

//     return (
//         <group name='AST-pillarRail-legs'>
//             <>
//                 <>
//                     {CSGSideRailGroupModelArr.leftModelArr.map((item, index) =>
//                         csgLeftLegIdArr.includes(index) &&
//                         <group key={`pillar-rail-${index}`}>
//                             <mesh name='outer' geometry={item.outer.geometry} position={[-(trussDimension.buildingWidth / 2 - railWidth / 2), 0, 0]}>
//                                 {  !!overlapTrussArr && overlapTrussArr.buildingType === 'center' && overlapTrussArr.idArr.includes(index) ?
//                                     <meshBasicMaterial renderOrder={-1} depthTest={false} color={'#C00000'} opacity={0.7} transparent />
//                                     :
//                                     <meshPhongMaterial color={"#F0F0F0"} map={normalMap} envMap={envMap} side={THREE.DoubleSide} reflectivity={0.4} />
//                                 }
//                             </mesh>
//                             <mesh name='inner' geometry={item.inner.geometry} position={[-(trussDimension.buildingWidth / 2 - railWidth / 2 - outInDistance), 0, 0]}>
//                                 <meshPhongMaterial color={"#F0F0F0"} map={normalMap} envMap={envMap} side={THREE.DoubleSide} reflectivity={0.4} />
//                             </mesh>
//                         </group>
//                     )}

//                     {/* --- left outer pillar model normal --- */}
//                     <instancedMesh ref={ref_leftOuterPillar} args={[null, null, trussModelPosInfoArr.length]} frustumCulled={false}>
//                         <extrudeGeometry args={[railModel(railWidth, railHeight), extrudeSettings(trussDimension.buildingHeight)]} />
//                         <meshPhongMaterial color={"#F0F0F0"} map={normalMap} envMap={envMap} side={THREE.DoubleSide} reflectivity={0.4} />
//                     </instancedMesh>
//                     {/* --- left inner pillar model normal --- */}
//                     <instancedMesh ref={ref_leftInnerPillar} args={[null, null, trussModelPosInfoArr.length]} frustumCulled={false}>
//                         <extrudeGeometry args={[railModel(railWidth, railHeight), extrudeSettings(trussDimension.buildingHeight + alignPosOffset)]} />
//                         <meshPhongMaterial color={"#F0F0F0"} map={normalMap} envMap={envMap} side={THREE.DoubleSide} reflectivity={0.4} />
//                     </instancedMesh>
//                     {/* --- left pillar highlight model normal --- */}
//                     {
//                         overlapTrussArr.idArr?.length > 0 && <instancedMesh instanceMatrix-needsUpdate={true} instanceMatrix={outerPillarHighlight.ref_leftOuterPillarHighlight} args={[null, null, trussModelPosInfoArr.length]} frustumCulled={false} >
//                             <extrudeGeometry args={[railModel(railWidth, railHeight), extrudeSettings(trussDimension.buildingHeight)]} />
//                             <meshBasicMaterial depthTest={false} color={'#900000'} opacity={0.7} transparent />
//                         </instancedMesh>
//                     }
//                     {/* ------------------------- */}
//                     {/* {CSGSideRailGroupModelArr.leftModelArr.map((item, index) => {
//                         let isOverlap = false;
//                         if (!!overlapTrussArr && overlapTrussArr.buildingType === 'center' && overlapTrussArr.idArr.includes(index)) isOverlap = true;

//                         return (
//                             <group key={`pillar-rail-${index}`}>
//                                 <mesh name='outer' geometry={item.outer.geometry} position={[-(trussDimension.buildingWidth / 2 - railWidth / 2), 0, 0]}>
//                                     <meshBasicMaterial renderOrder={-1} map={isOverlap ? null : normalMap} envMap={isOverlap ? null : envMap} depthTest={isOverlap ? false : true} reflectivity={0.4} color={isOverlap ? '#C00000' : "#F0F0F0"} opacity={isOverlap ? 0.7 : opacityValue} depthWrite alphaTest={'0.5'} />
//                                 </mesh>
//                                 <mesh name='inner' geometry={item.inner.geometry} position={[-(trussDimension.buildingWidth / 2 - railWidth / 2 - outInDistance), 0, 0]}>
//                                     <meshPhongMaterial color={"#F0F0F0"} map={normalMap} envMap={envMap} side={THREE.DoubleSide} reflectivity={0.4} opacity={leftLegOpcValue} depthWrite={leftIsLegOpcState} transparent={leftIsLegOpcState} needsUpdate={!leftIsLegOpcState} />
//                                 </mesh>
//                             </group>
//                         )}
//                     )} */}
//                     <>
//                         {/* {CSGSideRailGroupModelArr.leftConnectModelArr.map((item, index) => {
//                             const x = connectRailDataArr.count - 1; // The upper range from first and last x times
//                             const isEdge = index <= x || index >= CSGSideRailGroupModelArr.leftConnectModelArr.length - 1 - x;
                            
//                             return (!params.p_e_c_b || !isEdge) && (
//                                 <mesh
//                                     key={`connect-rail-${index}`}
//                                     geometry={item.geometry}
//                                     position={[-(trussDimension.buildingWidth / 2 - railWidth / 2), 0, 0]}
//                                 >
//                                     <meshPhongMaterial
//                                     color={"#F0F0F0"}
//                                     map={normalMap}
//                                     envMap={envMap}
//                                     side={THREE.DoubleSide}
//                                     reflectivity={0.4} opacity={leftLegOpcValue} depthWrite={leftIsLegOpcState} transparent={leftIsLegOpcState} needsUpdate={!leftIsLegOpcState}
//                                     />
//                                 </mesh>
//                             );
//                         })} */}
//                         <instancedMesh ref={ref_leftConnectRail} args={[null, null, trussModelPosInfoArr.length * (connectRailDataArr.dataInfoArray.length - 1)]} frustumCulled={false}>
//                             <extrudeGeometry args={[railModel(railHeight, railWidth), extrudeSettings(connectRailLength)]} />
//                             <meshPhongMaterial color={"#F0F0F0"} map={normalMap} envMap={envMap} side={THREE.DoubleSide} reflectivity={0.4} />
//                         </instancedMesh>
//                     </>
//                 </>
//                 <>
//                     {CSGSideRailGroupModelArr.rightModelArr.map((item, index) =>
//                         csgRightLegIdArr.includes(index) &&
//                         <group key={`pillar-rail-${index}`}>
//                             <mesh name='outer' geometry={item.outer.geometry} position={[(trussDimension.buildingWidth / 2 - railWidth / 2), 0, 0]}>
//                                 {  !!overlapTrussArr && overlapTrussArr.buildingType === 'center' && overlapTrussArr.idArr.includes(index) ?
//                                     <meshBasicMaterial renderOrder={-1} depthTest={false} color={'#C00000'} opacity={0.7} transparent />
//                                     :
//                                     <meshPhongMaterial color={"#F0F0F0"} map={normalMap} envMap={envMap} side={THREE.DoubleSide} reflectivity={0.4} />
//                                 }
//                             </mesh>
//                             <mesh name='inner' geometry={item.inner.geometry} position={[(trussDimension.buildingWidth / 2 - railWidth / 2 - outInDistance), 0, 0]}>
//                                 <meshPhongMaterial color={"#F0F0F0"} map={normalMap} envMap={envMap} side={THREE.DoubleSide} reflectivity={0.4} />
//                             </mesh>
//                         </group>
//                     )}
//                     {/* --- right outer pillar model normal --- */}
//                     <instancedMesh ref={ref_rightOuterPillar} args={[null, null, trussModelPosInfoArr.length]} frustumCulled={false}>
//                         <extrudeGeometry args={[railModel(railWidth, railHeight), extrudeSettings(trussDimension.buildingHeight)]} />
//                         <meshPhongMaterial color={"#F0F0F0"} map={normalMap} envMap={envMap} side={THREE.DoubleSide} reflectivity={0.4} />
//                     </instancedMesh>
//                     {/* --- right inner pillar model normal --- */}
//                     <instancedMesh ref={ref_rightInnerPillar} args={[null, null, trussModelPosInfoArr.length]} frustumCulled={false}>
//                         <extrudeGeometry args={[railModel(railWidth, railHeight), extrudeSettings(trussDimension.buildingHeight + alignPosOffset)]} />
//                         <meshPhongMaterial color={"#F0F0F0"} map={normalMap} envMap={envMap} side={THREE.DoubleSide} reflectivity={0.4} />
//                     </instancedMesh>
//                     {/* --- right pillar highlight model normal --- */}
//                     {
//                         overlapTrussArr.idArr?.length > 0 && <instancedMesh instanceMatrix-needsUpdate={true} instanceMatrix={outerPillarHighlight.ref_rightOuterPillarHighlight} args={[null, null, trussModelPosInfoArr.length]} frustumCulled={false} >
//                             <extrudeGeometry args={[railModel(railWidth, railHeight), extrudeSettings(trussDimension.buildingHeight)]} />
//                             <meshBasicMaterial depthTest={false} color={'#900000'} opacity={0.7} transparent />
//                         </instancedMesh>
//                     }
//                     {/* ------------------------- */}
//                     {/* {CSGSideRailGroupModelArr.rightModelArr.map((item, index) => {
//                         let isOverlap = false;
//                         if (!!overlapTrussArr && overlapTrussArr.buildingType === 'center' && overlapTrussArr.idArr.includes(index)) isOverlap = true;

//                         return (
//                             <group key={`pillar-rail-${index}`}>
//                                 <mesh name='outer' geometry={item.outer.geometry} position={[(trussDimension.buildingWidth / 2 - railWidth / 2), 0, 0]}>
//                                     <meshBasicMaterial renderOrder={-1} map={isOverlap ? null : normalMap} envMap={isOverlap ? null : envMap} depthTest={isOverlap ? false : true} reflectivity={0.4} color={isOverlap ? '#C00000' : "#F0F0F0"} opacity={isOverlap ? 0.7 : opacityValue} depthWrite alphaTest={'0.5'}  />
//                                 </mesh>
//                                 <mesh name='inner' geometry={item.inner.geometry} position={[(trussDimension.buildingWidth / 2 - railWidth / 2 - outInDistance), 0, 0]}>
//                                     <meshPhongMaterial color={"#F0F0F0"} map={normalMap} envMap={envMap} side={THREE.DoubleSide} reflectivity={0.4} opacity={rightLegOpcValue} depthWrite={rightIsLegOpcState} transparent={rightIsLegOpcState} needsUpdate={!rightIsLegOpcState} />
//                                 </mesh>
//                             </group>
//                         )}
//                     )} */}
//                     <>
//                         {/* {CSGSideRailGroupModelArr.rightConnectModelArr.map((item, index) => {
//                             const x = connectRailDataArr.count - 1; // The upper range from first and last x times
//                             const isEdge = index <= x || index >= CSGSideRailGroupModelArr.leftConnectModelArr.length - 1 - x;
                            
//                             return (!params.p_e_c_b || !isEdge) && (
//                                 <mesh
//                                     key={`connect-rail-${index}`}
//                                     geometry={item.geometry}
//                                     position={[(trussDimension.buildingWidth / 2 - railWidth / 2), 0, 0]}
//                                 >
//                                     <meshPhongMaterial
//                                     color={"#F0F0F0"}
//                                     map={normalMap}
//                                     envMap={envMap}
//                                     side={THREE.DoubleSide}
//                                     reflectivity={0.4} opacity={rightLegOpcValue} depthWrite={rightIsLegOpcState} transparent={rightIsLegOpcState} needsUpdate={!rightIsLegOpcState}
//                                     />
//                                 </mesh>
//                             )
//                         })} */}
//                         <instancedMesh ref={ref_rightConnectRail} args={[null, null, trussModelPosInfoArr.length * (connectRailDataArr.dataInfoArray.length - 1)]} frustumCulled={false}>
//                             <extrudeGeometry args={[railModel(railHeight, railWidth), extrudeSettings(connectRailLength)]} />
//                             <meshPhongMaterial color={"#F0F0F0"} map={normalMap} envMap={envMap} side={THREE.DoubleSide} reflectivity={0.4} />
//                         </instancedMesh>
//                     </>
//                 </>
//             </>
//             {params.p_e_c_b && 
//                 <>
//                     <>
//                         {CSGFrontBackPillarRailModelArr.frontEndCrossBracingLegsArr.map((item, index_front) =>
//                             <mesh key={`front-leg-rail-${index_front}`} geometry={item.geometry} position={[0, 0, params.p_d / 2]}>
//                                 <meshPhongMaterial color={"#F0F0F0"} map={normalMap} envMap={envMap} side={THREE.DoubleSide} reflectivity={0.4}  />
//                             </mesh>
//                         )}
//                         {CSGFrontBackPillarRailModelArr.frontEndCrossBracingHorizonTrussArr.map((item, index_front_horizon) =>
//                             <mesh key={`front-horizon-rail-${index_front_horizon}`} geometry={item.geometry} position={[0, 0, params.p_d / 2]}>
//                                 <meshPhongMaterial color={"#F0F0F0"} map={normalMap} envMap={envMap} side={THREE.DoubleSide} reflectivity={0.4}  />
//                             </mesh>
//                         )}
//                         {CSGFrontBackPillarRailModelArr.frontEndCrossBracingSlopeTrussArr.map((item, index_front_vertical) =>
//                             <mesh key={`front-vertical-rail-${index_front_vertical}`} geometry={item.geometry} position={[0, 0, params.p_d / 2]}>
//                                 <meshPhongMaterial color={"#F0F0F0"} map={normalMap} envMap={envMap} side={THREE.DoubleSide} reflectivity={0.4}  />
//                             </mesh>
//                         )}
//                     </>
//                     <>
//                         {CSGFrontBackPillarRailModelArr.backEndCrossBracingLegsArr.map((item, index_back_leg) =>
//                             <mesh key={`back-leg-rail-${index_back_leg}`} geometry={item.geometry} position={[0, 0, -params.p_d / 2]}>
//                                 <meshPhongMaterial color={"#F0F0F0"} map={normalMap} envMap={envMap} side={THREE.DoubleSide} reflectivity={0.4}  />
//                             </mesh>
//                         )}
//                         {CSGFrontBackPillarRailModelArr.backEndCrossBracingHorizonTrussArr.map((item, index_back_horizon) =>
//                             <mesh key={`back-horizon-rail-${index_back_horizon}`} geometry={item.geometry} position={[0, 0, -params.p_d / 2]}>
//                                 <meshPhongMaterial color={"#F0F0F0"} map={normalMap} envMap={envMap} side={THREE.DoubleSide} reflectivity={0.4}  />
//                             </mesh>
//                         )}
//                         {CSGFrontBackPillarRailModelArr.backEndCrossBracingSlopeTrussArr.map((item, index_back_vertical) =>
//                             <mesh key={`back-vertical-rail-${index_back_vertical}`} geometry={item.geometry} position={[0, 0, -params.p_d / 2]}>
//                                 <meshPhongMaterial color={"#F0F0F0"} map={normalMap} envMap={envMap} side={THREE.DoubleSide} reflectivity={0.4}  />
//                             </mesh>
//                         )}
//                     </>
//                 </>
//             }
//             {params.p_s_c_b && 
//                 <>
//                     <>
//                         {CSGSideRailGroupModelArr.leftSideCrossBracingHorizonTrussArr.map((item, index_side_horizon) =>
//                             <mesh key={`side-horizon-rail-${index_side_horizon}`} geometry={item.geometry} position={[-trussDimension.buildingWidth / 2 + railWidth / 2, 0, 0]}>
//                                 <meshPhongMaterial color={"#F0F0F0"} map={normalMap} envMap={envMap} side={THREE.DoubleSide} reflectivity={0.4}  />
//                             </mesh>
//                         )}
//                         {CSGSideRailGroupModelArr.leftSideCrossBracingSlopeTrussArr.map((item, index_side_vertical) =>
//                             <mesh key={`side-vertical-rail-${index_side_vertical}`} geometry={item.geometry} position={[-trussDimension.buildingWidth / 2 + railWidth / 2, 0, 0]}>
//                                 <meshPhongMaterial color={"#F0F0F0"} map={normalMap} envMap={envMap} side={THREE.DoubleSide} reflectivity={0.4}  />
//                             </mesh>
//                         )}
//                     </>
//                     <>
//                         {CSGSideRailGroupModelArr.rightSideCrossBracingHorizonTrussArr.map((item, index_side_horizon) =>
//                             <mesh key={`side-horizon-rail-${index_side_horizon}`} geometry={item.geometry} position={[trussDimension.buildingWidth / 2 - railWidth / 2, 0, 0]}>
//                                 <meshPhongMaterial color={"#F0F0F0"} map={normalMap} envMap={envMap} side={THREE.DoubleSide} reflectivity={0.4}  />
//                             </mesh>
//                         )}
//                         {CSGSideRailGroupModelArr.rightSideCrossBracingSlopeTrussArr.map((item, index_side_vertical) =>
//                             <mesh key={`side-vertical-rail-${index_side_vertical}`} geometry={item.geometry} position={[trussDimension.buildingWidth / 2 - railWidth / 2, 0, 0]}>
//                                 <meshPhongMaterial color={"#F0F0F0"} map={normalMap} envMap={envMap} side={THREE.DoubleSide} reflectivity={0.4}  />
//                             </mesh>
//                         )}
//                     </>
//                 </>
//             }

//             {params.p_f_w === 'Close' &&
//                 <>
//                     {CSGFrontBackPillarRailModelArr.frontModelArr.map((item, index_front) =>
//                         <mesh key={`front-vertical-rail-${index_front}`} geometry={item.geometry} position={[0, 0, params.p_d / 2]}>
//                             <meshPhongMaterial color={"#F0F0F0"} map={normalMap} envMap={envMap} side={THREE.DoubleSide} reflectivity={0.4} />
//                         </mesh>
//                     )}
//                     <mesh name='front-bottom' geometry={CSGFrontBackBottomRailData.CSGFrontBottomRailModel.geometry} position={[0, 0, params.p_d / 2]}>
//                         <meshPhongMaterial color={"#F0F0F0"} map={normalMap} envMap={envMap} side={THREE.DoubleSide} reflectivity={0.4} />
//                     </mesh>
//                 </>
//             }
            
//             {(params.p_b_w === 'Close' || params.p_u_c) &&
//                 <>
//                     {CSGFrontBackPillarRailModelArr.backModelArr.map((item, index_front) =>
//                         <mesh key={`back-vertical-rail-${index_front}`} geometry={item.geometry} position={[0, 0, -params.p_d / 2]}>
//                             <meshPhongMaterial color={"#F0F0F0"} map={normalMap} envMap={envMap} side={THREE.DoubleSide} reflectivity={0.4}  />
//                         </mesh>
//                     )}
//                     <mesh name='back-bottom' geometry={CSGFrontBackBottomRailData.CSGBackBottomRailModel.geometry} position={[0, 0, -params.p_d / 2]}>
//                         <meshPhongMaterial color={"#F0F0F0"} map={normalMap} envMap={envMap} side={THREE.DoubleSide} reflectivity={0.4}  />
//                     </mesh>
//                 </>
//             }
//         </group>
//     )
// }

// const BaseRailModel = ({ normalMap, envMap, meshDelModel_CSG }) => {
//     const obj_baseRail = new THREE.Object3D();

//     const ref_baseRail = useRef();

//     const railWidth = 0.2;
//     const railHeight = 0.2;
//     const outInDistance = 0.5;

//     const roofAlpha = Math.atan(trussDimension.roofPitch / 12);

//     const alignPosOffset = useMemo(() => {
//         if(params.p_r_s === 1 ) return 0.36 + (trussDimension.roofPitch - 3) * 0.07 / 3;
//         else if(params.p_r_s === 2 || params.p_r_s === 3) return (trussDimension.roofPitch - 3) * 0.04;
//         else return 0;
//     }, [params.p_r_s, trussDimension.roofPitch])

//     const roofBaseRailArr = useMemo(() => {
//         let dataInfoArray = [];
//         let count, distance;

//         const initRoofSpace = 0.5;
//         let initDistance = 3.5;
//         const tempCount = Math.floor(((trussDimension.buildingWidth / 2 - initRoofSpace)) / initDistance);
//         if (((trussDimension.buildingWidth / 2 - initRoofSpace)) === tempCount * initDistance) {
//             count = tempCount;
//             distance = initDistance;
//         } else if (((trussDimension.buildingWidth / 2 - initRoofSpace)) - tempCount * initDistance <= initDistance / 2) {
//             count = tempCount;
//             distance = ((trussDimension.buildingWidth / 2 - initRoofSpace)) / count;
//         } else {
//             count = tempCount + 1;
//             distance = ((trussDimension.buildingWidth / 2 - initRoofSpace)) / count;
//         }

//         for (let i = 0; i < count; i++) {
//             const pos_x = initRoofSpace + distance * i - railWidth / 2;
//             const pos_y = trussDimension.buildingHeight + ((trussDimension.buildingWidth / 2 - initRoofSpace) - pos_x) * (trussDimension.roofPitch / 12) + railHeight / 2;
            
//             const tempInfoObj = {
//                 distance: distance,
//                 pos_x: pos_x,
//                 pos_y: pos_y,
//                 length: params.p_d
//             }

//             dataInfoArray.push(tempInfoObj);
//         }

//         return dataInfoArray
//     }, [trussDimension.buildingHeight, params.p_d, trussDimension.buildingWidth, trussDimension.roofPitch])

//     const baseRailLength = useMemo(() => {
//         let baseRailLength;
//         roofBaseRailArr.map((item_roof_rail) => {
//             baseRailLength = item_roof_rail.length
//         })
//         return baseRailLength
//     }, [roofBaseRailArr])
    
//     const CSGSideBottomRailData = useMemo(() => {
//         const modelShape = railModel(railWidth, railHeight);

//         const meshBottomRailModel = new THREE.Mesh(
//             new THREE.ExtrudeGeometry(modelShape, extrudeSettings(params.p_d)),
//             new THREE.MeshStandardMaterial({ color: 'pink' })
//         )
        
//         let meshLeftBottomRailModel_Temp = meshBottomRailModel.clone();    
//         meshLeftBottomRailModel_Temp.position.set(0, railWidth / 2, -params.p_d / 2);
//         meshLeftBottomRailModel_Temp.updateMatrix();
//         meshLeftBottomRailModel_Temp.updateMatrixWorld();
//         const meshLeftBottomRailModel_CSG = CSG.fromMesh(meshLeftBottomRailModel_Temp);

//         let meshRightBottomRailModel_Temp = meshBottomRailModel.clone();    
//         meshRightBottomRailModel_Temp.position.set(0, railWidth / 2, -params.p_d / 2);
//         meshRightBottomRailModel_Temp.updateMatrix();
//         meshRightBottomRailModel_Temp.updateMatrixWorld();
//         const meshRightBottomRailModel_CSG = CSG.fromMesh(meshRightBottomRailModel_Temp);

//         const leftBottomRailModel_CSG = meshLeftBottomRailModel_CSG.subtract(meshDelModel_CSG.left);
//         const rightBottomRailModel_CSG = meshRightBottomRailModel_CSG.subtract(meshDelModel_CSG.right);

//         const CSGLeftBottomRailModel = CSG.toMesh(
//             leftBottomRailModel_CSG,
//             new THREE.Matrix4(),
//             new THREE.MeshStandardMaterial({ color: 'blue' })
//         )

//         const CSGRightBottomRailModel = CSG.toMesh(
//             rightBottomRailModel_CSG,
//             new THREE.Matrix4(),
//             new THREE.MeshStandardMaterial({ color: 'blue' })
//         )

//         return { CSGLeftBottomRailModel, CSGRightBottomRailModel };
//     }, [params.p_d, meshDelModel_CSG.left, meshDelModel_CSG.right])
    

//     useEffect(() => {
//         if (ref_baseRail.current) {
//             let index_all = 0;
            
//             new Array(2).fill('').forEach((_, index_dir) => {
//                 let offsetForDir = 1;
//                 if (index_dir === 1) offsetForDir = 1;
//                 else offsetForDir = -1;

//                 roofBaseRailArr.map((item_roof_rail, index_truss) => {
//                     //----- base rail -----
//                     obj_baseRail.position.set(offsetForDir * (item_roof_rail.pos_x), item_roof_rail.pos_y + alignPosOffset, -params.p_d / 2);
//                     obj_baseRail.rotation.z = offsetForDir * -roofAlpha;

//                     obj_baseRail.updateMatrix();
//                     ref_baseRail.current.setMatrixAt(index_all, obj_baseRail.matrix);
//                     //---------------------
//                     index_all ++;
//                 })
//             })
//             ref_baseRail.current.instanceMatrix.needsUpdate = true;
//         }
//     }, [ref_baseRail.current, roofBaseRailArr, roofAlpha, params.p_d, params.p_r_s, alignPosOffset])

//     return (
//         <group name='AST-baserail-legs'>
//             <>
//                 <mesh name='left-outer' geometry={CSGSideBottomRailData.CSGLeftBottomRailModel.geometry} position={[-(trussDimension.buildingWidth / 2 - railHeight / 2), 0, 0]}>
//                     <meshPhongMaterial color={"#F0F0F0"} map={normalMap} envMap={envMap} side={THREE.DoubleSide} reflectivity={0.4}  />
//                 </mesh>
//                 <mesh name='left-inner' geometry={CSGSideBottomRailData.CSGLeftBottomRailModel.geometry} position={[-(trussDimension.buildingWidth / 2 - railHeight / 2 - outInDistance), 0, 0]}>
//                     <meshPhongMaterial color={"#F0F0F0"} map={normalMap} envMap={envMap} side={THREE.DoubleSide} reflectivity={0.4}  />
//                 </mesh>
//                 <mesh name='right-outer' geometry={CSGSideBottomRailData.CSGRightBottomRailModel.geometry} position={[(trussDimension.buildingWidth / 2 - railHeight / 2), 0, 0]}>
//                     <meshPhongMaterial color={"#F0F0F0"} map={normalMap} envMap={envMap} side={THREE.DoubleSide} reflectivity={0.4}  />
//                 </mesh>
//                 <mesh name='right-inner' geometry={CSGSideBottomRailData.CSGRightBottomRailModel.geometry} position={[(trussDimension.buildingWidth / 2 - railHeight / 2 - outInDistance), 0, 0]}>
//                     <meshPhongMaterial color={"#F0F0F0"} map={normalMap} envMap={envMap} side={THREE.DoubleSide} reflectivity={0.4}  />
//                 </mesh>
//             </>

//             {new Array(2).fill('').map((_, index_invert) =>
//                 <group name='base-rail-model-legs' key={`base-rail-model-${index_invert}`}>
//                     <mesh name='unper' position={[Math.pow(-1, index_invert) * (trussDimension.buildingWidth / 2 - railHeight / 2), trussDimension.buildingHeight - railHeight / 2, -params.p_d / 2]}>
//                         <extrudeGeometry args={[railModel(railWidth, railHeight), extrudeSettings(params.p_d)]} />
//                         <meshPhongMaterial color={"#F0F0F0"} map={normalMap} envMap={envMap} side={THREE.DoubleSide} reflectivity={0.4}  />
//                     </mesh>
//                 </group>
//             )}

//             {/* --- roof base rail model --- */}
//             <instancedMesh ref={ref_baseRail} args={[null, null, roofBaseRailArr.length * 2]} frustumCulled={false}>
//                 <extrudeGeometry args={[roofBaseRailModel(), extrudeSettings(baseRailLength)]} />
//                 <meshPhongMaterial color={"#F0F0F0"} map={normalMap} envMap={envMap} side={THREE.DoubleSide} reflectivity={0.4} />
//             </instancedMesh>
//             {/* ------------------------- */}
//         </group>
//     )
// }
